import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { BFF_URL, envIsCanada } from '@/constants/constants';
import { RootState } from '@/models/root-state.model';
import { Store } from 'redux';
import { BFF_API_URLS } from '@/constants/bff-api-urls';
import {
  BffCompleteProfileMexicoRequest,
  BffGamesFetchGameUrlResponse,
  BffGamesGetGameUrlRequest,
  BffGamesJackpotsResponse,
  BffGamesResponse,
  BffRegisterResponse,
  BffResponse,
  BffVerifyUserResponse,
  BffGamesLastPlayedResponse,
  BffGetSowqResponse,
  BffPostSowqRequest,
  BffUserKycResponse,
  BffForgotPasswordRequest,
  BffForgotPasswordChangeRequest,
  BffSetAuthenticationMethodPathParams,
  BffAuthenticationMethodResponse,
  BffValidateEmailPhoneRequestType,
  BffValidateEmailPhoneResponse,
  BffCmsPromotionsResponse,
  BffStaticTextContentResponse,
  BffCompleteProfileRequest,
  BffCmsBonusIntroCardsResponse,
  BffPsaResponse,
  BffCmsFaqResponse,
  BffCmsWelcomeTextsResponse,
  BffStaticTextContentQueryParams,
  BffBonusCreditPathParams,
  BffKycFilesUploadPathParams,
  BffWalletResponse,
  BffRegisterAgcoRequestV2,
  BffComplianceOntarioCheckResponse,
  BffRegisterThRequest,
  BffForgotPasswordSmsRequest,
  BffForgotPasswordSmsChangeRequest,
  BffCompleteProfileThailandRequest,
  BffAuthResponse,
  BffAuthOtpCodeRequest,
  BffRegisterMxNoCurpRequest,
  BffCompleteProfileMexicoCurpRequest,
  BffBonusForfeitPathParams,
  BffAddressLookupRequest,
  BffAddressLookupResponse,
  BffAddressRetrieveRequest,
  BffAddressRetrieveResponse,
  BffIndustriesAndOccupationQueryParams,
  BffIndustriesAndOccupationsResponse,
  BffUserComplianceOntarioRequest,
  BffUserComplianceOntarioResponse,
  BffChangePasswordRequest,
  BffCountriesResponse,
  BffUserConsentsResponse,
  BffUpdateUserConsentsRequest,
  BffTotalJackpotPerCurrencyResponse,
  BffTotalJackpotPerCurrencyPathParams,
  BffGamesTransactionsRequest,
  BffGamesTransactionsResponse,
  BffGenerateCheckinIdScanDataResponse,
  BffResponsibleGamingAddTimeoutRequest,
  BffCmsHeaderBannersResponse,
  BffLoginHistoryResponse,
  BffUserTagsResponse,
  BffRealityCheckResponse,
  BffUserLimitsResponse,
  BffResponsibleGamingSetUserLimitsRequest,
  BffFavouriteGamesResponse,
  BffFavouriteGamesAddPathParams,
  BffResponsibleGamingSetRealityCheckRequest,
  BffUserResponse,
  BffCmsJackpotResponse,
  BffResponsibleGamingSetSelfExclusionRequest,
  BffUserPlayStatsRequestQueryParams,
  BffUserPlayStatsResponse,
  BffCmsHeroBackgroundResponse,
  BffCmsHeroBackgroundQueryParams,
  BffUserAmlVerificationStatusResponse,
  BffRegisterFastRequest,
  BffAgcoUserDetailsResponse,
  BffWalletTransactionsQueryParams,
  BffWalletTransactionsResponse,
  BffUserGracePeriodResponse,
  BffUserUpdateRequest,
  BffFetchBonusesRequest,
  BffFetchBonusesResponse,
  BffUserUpdateEmailRequest,
  BffUserUpdateContactInfoRequest,
  BffUserUpdateContactInfoResponse,
  BffAuthWithChallengeRequest,
  BffAuthWithChallengeResponse,
  BffAuthRequest,
  BffSubdivisionsResponseV2,
  BffSubdivisionsPerCountryCodePathParams,
  BffErrorCode,
  BffGetIdScanStatusPathParams,
  BffGetIdScanStatusResponse,
  BffAuthSessionCheckResponse,
  BffAgcoVerifyUpdateRequest,
  BffUserAgcoVerifyUpdateResponse,
} from '@lucky7ventures/bff-types';
import { RegionCheckResponse } from '@/models/region-check.model';
import { logout } from '@/store/actions/auth';

const axiosInstance = axios.create({
  baseURL: BFF_URL,
});

const getRequestLocale = (locale: string) => {
  if (envIsCanada()) {
    return `${locale}-on`;
  }

  switch (locale) {
    case 'eu':
      return 'row';
    default:
      return locale;
  }
};

export function bffApiServiceInterceptor(store: Store) {
  axiosInstance.interceptors.request.use(request => {
    const state: RootState = store.getState();
    const locale = state.common.locale;
    const token = state.auth.token;
    let headers = request.headers;

    // A singular place in the app where we attach the token to the request for API V2
    if (token) {
      headers = { ...headers, 'l7-t': `${token}` };
    }

    const requestLocale = getRequestLocale(locale);

    headers = {
      ...headers,
      'l7-b': '1', // 1 is for LuckyDays
      'l7-locale': requestLocale,
    };
    request.headers = headers;

    // add an additional "l" param to each request -> used for browser cache busting on the browser level
    // do it only for GET requests
    if (request.method === 'get' && requestLocale) {
      const additionalParams = {
        l: requestLocale,
      };
      request.params = {
        ...request.params,
        ...additionalParams,
      };
    }
    return request;
  });

  axiosInstance.interceptors.response.use(
    response => response,
    (error: AxiosError<any>) => {
      // Logout endpoint is a filter here because currently
      // we do the actual logout endpoint call when the logout
      // redux action is invoked (via the saga). At this point
      // in time we won't refactor that.
      // That logout endpoint call is needed only when user
      // manually logs out via the UI button on the details page.
      if (
        error.response &&
        !error.response.config.url?.includes(BFF_API_URLS.LOGOUT) &&
        error.response.status === 401 &&
        error.response.data &&
        error.response.data.error &&
        error.response.data.error.code === BffErrorCode.GIG_INVALID_AUTHENTICATION_TOKEN
      ) {
        store.dispatch(logout());
      }

      return Promise.reject(error);
    },
  );
}

export type ApiServiceMethod<R> = (arg?: {
  config?: AxiosRequestConfig;
}) => Promise<AxiosResponse<BffResponse<R>>>;

export type ApiServiceMethodPayload<D, R> = ({
  payload,
  config,
}: {
  payload: D;
  config?: AxiosRequestConfig<D>;
}) => Promise<AxiosResponse<BffResponse<R>>>;

export type ApiServiceMethodPathParams<P, R> = ({
  pathParams,
  config,
}: {
  pathParams: P;
  config?: AxiosRequestConfig;
}) => Promise<AxiosResponse<BffResponse<R>>>;

export type ApiServiceMethodPayloadPathParams<D, P, R> = ({
  payload,
  pathParams,
  config,
}: {
  payload: D;
  pathParams: P;
  config?: AxiosRequestConfig<D>;
}) => Promise<AxiosResponse<BffResponse<R>>>;

export type ApiServiceMethodQueryParams<Q, R> = ({
  config,
}: {
  config?: AxiosRequestConfig & { params: Q };
}) => Promise<AxiosResponse<BffResponse<R>>>;

export interface BffApiServiceI {
  registerMexicoNoCurp: ApiServiceMethodPayload<BffRegisterMxNoCurpRequest, BffRegisterResponse>;
  registerAgco: ApiServiceMethodPayload<BffRegisterAgcoRequestV2, BffRegisterResponse>;
  completeProfileMexico: ApiServiceMethodPayload<BffCompleteProfileMexicoRequest, null>;
  completeProfileMexicoV3: ApiServiceMethodPayload<
    {
      phone: {
        prefix: string;
        number: string;
      };
    },
    null
  >;
  completeProfileMexicoCurp: ApiServiceMethodPayload<BffCompleteProfileMexicoCurpRequest, null>;
  verifyUser: ApiServiceMethod<BffVerifyUserResponse>;
  uploadKycDocuments: ApiServiceMethodPayloadPathParams<
    FormData,
    BffKycFilesUploadPathParams,
    null
  >;
  changePassword: ApiServiceMethodPayload<BffChangePasswordRequest, BffResponse<null>>;
  // TODO: Update type once @lucky7ventures/bff-types removes BffGamesRequest
  getAllGames: ApiServiceMethod<BffGamesResponse>;
  getFavouriteGames: ApiServiceMethod<BffFavouriteGamesResponse>;
  favouriteGame: ApiServiceMethodPathParams<BffFavouriteGamesAddPathParams, null>;
  removeFromFavouriteGames: ApiServiceMethodPathParams<BffFavouriteGamesAddPathParams, null>;
  getGamesJackpots: ApiServiceMethod<BffGamesJackpotsResponse>;
  getLoginHistory: ApiServiceMethod<BffLoginHistoryResponse>;
  getGamesLastPlayed: ApiServiceMethod<BffGamesLastPlayedResponse>;
  getGameUrl: ApiServiceMethodPayload<BffGamesGetGameUrlRequest, BffGamesFetchGameUrlResponse>;
  getKycSowq: ApiServiceMethod<BffGetSowqResponse>;
  postKycSowq: ApiServiceMethodPayload<BffPostSowqRequest, null>;
  forgotPassword: ApiServiceMethodPayload<BffForgotPasswordRequest, null>;
  forgotPasswordChange: ApiServiceMethodPayload<BffForgotPasswordChangeRequest, null>;
  forgotPasswordSms: ApiServiceMethodPayload<BffForgotPasswordSmsRequest, null>;
  forgotPasswordSmsChange: ApiServiceMethodPayload<BffForgotPasswordSmsChangeRequest, null>;
  logout: ApiServiceMethod<null>;
  getAuthenticationMethod: ApiServiceMethod<BffAuthenticationMethodResponse>;
  setAuthenticationMethod: ApiServiceMethodPathParams<BffSetAuthenticationMethodPathParams, null>;
  getKycDocuments: ApiServiceMethod<BffUserKycResponse>;
  validateEmailPhone: ApiServiceMethodPayload<
    BffValidateEmailPhoneRequestType,
    BffValidateEmailPhoneResponse
  >;
  generateIdScanData: ApiServiceMethod<BffGenerateCheckinIdScanDataResponse>;
  startCheckinIdScan: ApiServiceMethod<{ token: string; idScanSessionId: string }>;
  getFaq: ApiServiceMethod<BffCmsFaqResponse>;
  getHeaderBanners: ApiServiceMethod<BffCmsHeaderBannersResponse>;
  getBonusIntroCards: ApiServiceMethod<BffCmsBonusIntroCardsResponse>;
  fetchBonuses: ApiServiceMethodPayload<BffFetchBonusesRequest, BffFetchBonusesResponse>;
  getPromotions: ApiServiceMethod<BffCmsPromotionsResponse>;
  getWallet: ApiServiceMethod<BffWalletResponse>;
  getStaticTextContent: ApiServiceMethodQueryParams<
    BffStaticTextContentQueryParams,
    BffStaticTextContentResponse
  >;
  completeProfile: ApiServiceMethodPayload<BffCompleteProfileRequest, null>;
  getPsa: ApiServiceMethod<BffPsaResponse>;
  getWelcomeTexts: ApiServiceMethod<BffCmsWelcomeTextsResponse>;
  creditBonus: ApiServiceMethodPathParams<BffBonusCreditPathParams, null>;
  ontarioCheck: ApiServiceMethod<BffComplianceOntarioCheckResponse>;
  regionCheck: ApiServiceMethod<RegionCheckResponse>;
  registerThailand: ApiServiceMethodPayload<BffRegisterThRequest, BffRegisterResponse>;
  completeProfileThailand: ApiServiceMethodPayload<BffCompleteProfileThailandRequest, null>;
  login: ApiServiceMethodPayload<BffAuthRequest, BffAuthResponse>;
  loginOtp: ApiServiceMethodPayload<BffAuthOtpCodeRequest, BffAuthResponse>;
  forfeitBonus: ApiServiceMethodPathParams<BffBonusForfeitPathParams, null>;
  addressLookup: ApiServiceMethodPayload<BffAddressLookupRequest, BffAddressLookupResponse>;
  addressRetrieval: ApiServiceMethodPayload<BffAddressRetrieveRequest, BffAddressRetrieveResponse>;
  fetchIndustriesAndOccupations: ApiServiceMethodQueryParams<
    BffIndustriesAndOccupationQueryParams,
    BffIndustriesAndOccupationsResponse
  >;
  checkOntarioCompliance: ApiServiceMethodPayload<
    BffUserComplianceOntarioRequest,
    BffUserComplianceOntarioResponse
  >;
  getCountries: ApiServiceMethod<BffResponse<BffCountriesResponse>>;
  getConsents: ApiServiceMethod<BffUserConsentsResponse>;
  updateConsents: ApiServiceMethodPayload<BffUpdateUserConsentsRequest, BffUserConsentsResponse>;
  fetchTotalJackpot: ApiServiceMethodPathParams<
    BffTotalJackpotPerCurrencyPathParams,
    BffTotalJackpotPerCurrencyResponse
  >;
  fetchGamesTransactions: ApiServiceMethodQueryParams<
    BffGamesTransactionsRequest,
    BffGamesTransactionsResponse
  >;
  getIdScanStatus: ApiServiceMethodPathParams<
    BffGetIdScanStatusPathParams,
    BffGetIdScanStatusResponse
  >;
  addTimeout: ApiServiceMethodPayload<BffResponsibleGamingAddTimeoutRequest, null>;
  getRealityCheck: ApiServiceMethod<BffRealityCheckResponse>;
  getUserTags: ApiServiceMethod<BffResponse<BffUserTagsResponse>>;
  getUserLimits: ApiServiceMethod<BffUserLimitsResponse>;
  setUserLimits: ApiServiceMethodPayload<BffResponsibleGamingSetUserLimitsRequest, null>;
  setRealityCheck: ApiServiceMethodPayload<BffResponsibleGamingSetRealityCheckRequest, null>;
  addPanicTimeout: ApiServiceMethod<null>;
  getUser: ApiServiceMethod<BffUserResponse>;
  getCmsJackpot: ApiServiceMethod<BffCmsJackpotResponse>;
  addSelfExclusion: ApiServiceMethodPayload<BffResponsibleGamingSetSelfExclusionRequest, null>;
  getUserPlayStats: ApiServiceMethodQueryParams<
    BffUserPlayStatsRequestQueryParams,
    BffUserPlayStatsResponse
  >;
  getCmsHeroBackgrounds: ApiServiceMethodQueryParams<
    BffCmsHeroBackgroundQueryParams,
    BffCmsHeroBackgroundResponse
  >;
  getUserAmlVerificationStatus: ApiServiceMethod<BffUserAmlVerificationStatusResponse>;
  registerFast: ApiServiceMethodPayload<BffRegisterFastRequest, BffRegisterResponse>;
  getAgcoUserDetails: ApiServiceMethod<BffAgcoUserDetailsResponse>;
  agcoVerify: ApiServiceMethod<null>;
  fetchWalletTransactions: ApiServiceMethodQueryParams<
    BffWalletTransactionsQueryParams,
    BffWalletTransactionsResponse
  >;
  fetchGracePeriod: ApiServiceMethod<BffUserGracePeriodResponse>;
  updateUser: ApiServiceMethodPayload<BffUserUpdateRequest, BffUserResponse>;
  changeUserEmail: ApiServiceMethodPayload<BffUserUpdateEmailRequest, null>;
  updateContactInfo: ApiServiceMethodPayload<
    BffUserUpdateContactInfoRequest,
    BffUserUpdateContactInfoResponse
  >;
  authenticateWithChallenge: ApiServiceMethodPayload<
    BffAuthWithChallengeRequest,
    BffAuthWithChallengeResponse
  >;
  getSubdivisions: ApiServiceMethodPathParams<
    BffSubdivisionsPerCountryCodePathParams,
    BffSubdivisionsResponseV2
  >;
  checkSession: ApiServiceMethod<BffAuthSessionCheckResponse>;
  agcoVerifyUpdate: ApiServiceMethodPayload<
    BffAgcoVerifyUpdateRequest,
    BffUserAgcoVerifyUpdateResponse
  >;
  mobileNumberVerificationRequired: ApiServiceMethodQueryParams<
    {
      btag: string;
    },
    {
      isMobileNumberVerificationRequired: boolean;
    }
  >;
  limitsRefund: ApiServiceMethodPayload<BffResponsibleGamingSetUserLimitsRequest, null>;
  sendSeonFraudEvent: ApiServiceMethodPayload<
    {
      actionType: 'login' | 'register';
      session: string;
    },
    null
  >;
}

const apiGetCall = (url: string, config?: AxiosRequestConfig) => {
  return axiosInstance.get(url, config);
};

const apiPostCall = <P>(url: string, payload: P, config?: AxiosRequestConfig<P>) => {
  return axiosInstance.post(url, payload, config);
};

const apiPutCall = <P>(url: string, payload?: P, config?: AxiosRequestConfig<P>) => {
  return axiosInstance.put(url, payload, config);
};

const apiDeleteCall = <P>(url: string, config?: AxiosRequestConfig<P>) => {
  return axiosInstance.delete(url, config);
};

const apiPatchCall = <P>(url: string, payload?: P, config?: AxiosRequestConfig<P>) => {
  return axiosInstance.patch(url, payload, config);
};

const BffApiService: BffApiServiceI = {
  registerMexicoNoCurp: ({ payload, config }) =>
    apiPostCall(BFF_API_URLS.REGISTER_MEXICO_2, payload, config),
  registerAgco: ({ payload, config }) => apiPostCall(BFF_API_URLS.REGISTER_AGCO, payload, config),
  completeProfileMexico: ({ payload, config }) =>
    apiPostCall(BFF_API_URLS.COMPLETE_PROFILE_MEXICO, payload, config),
  completeProfileMexicoV3: ({ payload, config }) =>
    apiPutCall(BFF_API_URLS.COMPLETE_PROFILE_MEXICO_3, payload, config),
  completeProfileMexicoCurp: ({ payload, config }) =>
    apiPostCall(BFF_API_URLS.COMPLETE_PROFILE_MEXICO_2, payload, config),
  verifyUser: arg => apiPostCall(BFF_API_URLS.VERIFY, null, arg?.config),
  uploadKycDocuments: ({ payload, pathParams, config }) =>
    apiPostCall(BFF_API_URLS.KYC_DOCUMENTS_UPLOAD(pathParams), payload, config),
  getAllGames: (args?: { config?: AxiosRequestConfig }) => {
    return apiGetCall(BFF_API_URLS.GET_ALL_GAMES, args?.config);
  },
  getFavouriteGames: args => apiPostCall(BFF_API_URLS.GET_FAVOURITE_GAMES, null, args?.config),
  favouriteGame: ({ pathParams, config }) =>
    apiPostCall(BFF_API_URLS.FAVOURITE_GAME(pathParams), null, config),
  removeFromFavouriteGames: ({ pathParams, config }) =>
    apiPostCall(BFF_API_URLS.REMOVE_FROM_FAVOURITE_GAMES(pathParams), null, config),
  getGamesJackpots: args => {
    return apiGetCall(BFF_API_URLS.GET_GAMES_JACKPOTS, args?.config);
  },
  getLoginHistory: arg => apiGetCall(BFF_API_URLS.GET_LOGIN_HISTORY, arg?.config),
  getGamesLastPlayed: arg => apiPostCall(BFF_API_URLS.GET_LAST_PLAYED, null, arg?.config),
  fetchBonuses: ({ payload, config }) => apiPostCall(BFF_API_URLS.GET_BONUSES, payload, config),
  getGameUrl: ({ payload, config }) => apiPostCall(BFF_API_URLS.GET_GAME_URL, payload, config),
  getKycSowq: arg => apiGetCall(BFF_API_URLS.KYC_SOWQ, arg?.config),
  postKycSowq: ({ payload, config }) => apiPostCall(BFF_API_URLS.KYC_SOWQ, payload, config),
  forgotPassword: ({ payload, config }) => {
    return apiPostCall(BFF_API_URLS.FORGOT_PASSWORD, payload, config);
  },
  forgotPasswordChange: ({ payload, config }) =>
    apiPostCall(BFF_API_URLS.FORGOT_PASSWORD_CHANGE, payload, config),
  logout: arg => {
    return apiPostCall(BFF_API_URLS.LOGOUT, null, arg?.config);
  },
  getAuthenticationMethod: arg => {
    return apiPostCall(BFF_API_URLS.GET_AUTHENTICATION_METHOD, null, arg?.config);
  },
  setAuthenticationMethod: ({ pathParams, config }) => {
    return apiPostCall(BFF_API_URLS.SET_AUTHENTICATION_METHOD(pathParams), null, config);
  },
  getKycDocuments: arg => apiPostCall(BFF_API_URLS.GET_KYC, null, arg?.config),
  validateEmailPhone: ({ payload, config }) =>
    apiPostCall(BFF_API_URLS.VALIDATE_EMAIL_PHONE, payload, config),
  getPromotions: arg => apiGetCall(BFF_API_URLS.GET_PROMOTIONS, arg?.config),
  getBonusIntroCards: arg => apiGetCall(BFF_API_URLS.GET_BONUS_INTRO_CARDS, arg?.config),
  getStaticTextContent: arg => apiGetCall(BFF_API_URLS.GET_STATIC_TEXT_CONTENT, arg.config),
  completeProfile: ({ payload, config }) =>
    apiPostCall(BFF_API_URLS.COMPLETE_PROFILE, payload, config),
  generateIdScanData: arg => apiGetCall(BFF_API_URLS.GENERATE_ID_SCAN_DATA, arg?.config),
  startCheckinIdScan: arg => apiPostCall(BFF_API_URLS.START_CHECKIN_ID_SCAN, null, arg?.config),
  getPsa: arg => apiGetCall(BFF_API_URLS.GET_PSA, arg?.config),
  getFaq: arg => apiGetCall(BFF_API_URLS.GET_FAQ, arg?.config),
  getHeaderBanners: arg => apiGetCall(BFF_API_URLS.GET_HEADER_BANNERS, arg?.config),
  getWelcomeTexts: arg => apiGetCall(BFF_API_URLS.GET_WELCOME_TEXTS, arg?.config),
  getWallet: arg => apiGetCall(BFF_API_URLS.GET_WALLET, arg?.config),
  creditBonus: ({ pathParams, config }) =>
    apiPutCall(BFF_API_URLS.CREDIT_BONUS(pathParams), null, config),
  ontarioCheck: arg => apiGetCall(BFF_API_URLS.ONTARIO_CHECK, arg?.config),
  regionCheck: arg => apiGetCall(BFF_API_URLS.REGION_CHECK, arg?.config),
  registerThailand: ({ payload, config }) =>
    apiPostCall(BFF_API_URLS.REGISTER_THAILAND, payload, config),
  changePassword: ({ payload, config }) =>
    apiPostCall(BFF_API_URLS.CHANGE_PASSWORD, payload, config),
  forgotPasswordSms: ({ payload, config }) =>
    apiPostCall(BFF_API_URLS.FORGOT_PASSWORD_SMS, payload, config),
  forgotPasswordSmsChange: ({ payload, config }) =>
    apiPostCall(BFF_API_URLS.FORGOT_PASSWORD_SMS_CHANGE, payload, config),
  completeProfileThailand: ({ payload, config }) =>
    apiPostCall(BFF_API_URLS.COMPLETE_PROFILE_THAILAND, payload, config),
  login: ({ payload, config }) => apiPostCall(BFF_API_URLS.LOGIN, payload, config),
  loginOtp: ({ payload, config }) => apiPostCall(BFF_API_URLS.LOGIN_OTP, payload, config),
  forfeitBonus: ({ pathParams, config }) =>
    apiDeleteCall(BFF_API_URLS.FORFEIT_BONUS(pathParams), config),
  addressLookup: ({ payload, config }) => {
    return apiPostCall(BFF_API_URLS.ADDRESS_LOOKUP, payload, config);
  },
  addressRetrieval: ({ payload, config }) => {
    return apiPostCall(BFF_API_URLS.ADDRESS_RETRIEVAL, payload, config);
  },
  fetchIndustriesAndOccupations: ({ config }) => {
    return apiGetCall(BFF_API_URLS.FETCH_INDUSTRIES_AND_OCCUPATIONS, config);
  },
  checkOntarioCompliance: ({ payload, config }) =>
    apiPostCall(BFF_API_URLS.COMPLIANCE_CHECK_ONTARIO, payload, config),
  getCountries: arg => {
    return apiGetCall(BFF_API_URLS.GET_COUNTRIES, arg?.config);
  },
  getConsents: arg => {
    return apiGetCall(BFF_API_URLS.GET_CONSENTS, arg?.config);
  },
  updateConsents: ({ payload, config }) => {
    return apiPutCall(BFF_API_URLS.UPDATE_CONSENTS, payload, config);
  },
  fetchTotalJackpot: ({ pathParams, config }) => {
    return apiGetCall(BFF_API_URLS.FETCH_TOTAL_JACKPOT(pathParams), config);
  },
  fetchGamesTransactions: ({ config }) => {
    return apiGetCall(BFF_API_URLS.FETCH_GAMES_TRANSACTIONS, config);
  },
  getIdScanStatus: ({ config, pathParams }) => {
    return apiGetCall(BFF_API_URLS.GET_ID_SCAN_STATUS(pathParams), config);
  },
  addTimeout: ({ payload, config }) => {
    return apiPostCall(BFF_API_URLS.ADD_TIMEOUT, payload, config);
  },
  getRealityCheck: arg => {
    return apiGetCall(BFF_API_URLS.GET_REALITY_CHECK, arg?.config);
  },
  getUserTags: arg => {
    return apiGetCall(BFF_API_URLS.GET_USER_TAGS, arg?.config);
  },
  getUserLimits: arg => {
    return apiGetCall(BFF_API_URLS.GET_USER_LIMITS, arg?.config);
  },
  setUserLimits: ({ payload, config }) => {
    return apiPostCall(BFF_API_URLS.SET_USER_LIMITS, payload, config);
  },
  setRealityCheck: ({ payload, config }) => {
    return apiPostCall(BFF_API_URLS.SET_REALITY_CHECK, payload, config);
  },
  addPanicTimeout: arg => {
    return apiPostCall(BFF_API_URLS.ADD_PANIC_TIMEOUT, null, arg?.config);
  },
  getUser: args => {
    return apiGetCall(BFF_API_URLS.GET_USER, args?.config);
  },
  getCmsJackpot: args => {
    return apiGetCall(BFF_API_URLS.GET_JACKPOT, args?.config);
  },
  addSelfExclusion: ({ payload, config }) => {
    return apiPostCall(BFF_API_URLS.ADD_SELF_EXCLUSION, payload, config);
  },
  getUserPlayStats: ({ config }) => {
    return apiGetCall(BFF_API_URLS.GET_USER_PLAY_STATS, config);
  },
  getCmsHeroBackgrounds: ({ config }) => {
    return apiGetCall(BFF_API_URLS.GET_HERO_BACKGROUNDS, config);
  },
  getUserAmlVerificationStatus: arg => {
    return apiGetCall(BFF_API_URLS.GET_USER_AML_VERIFICATION_STATUS, arg?.config);
  },
  registerFast: ({ payload, config }) => apiPostCall(BFF_API_URLS.REGISTER_FAST, payload, config),
  getAgcoUserDetails: args => {
    return apiGetCall(BFF_API_URLS.GET_AGCO_USER_DETAILS, args?.config);
  },
  agcoVerify: args => {
    return apiPutCall(BFF_API_URLS.AGCO_USER_VERIFY, null, args?.config);
  },
  fetchWalletTransactions: arg => {
    return apiGetCall(BFF_API_URLS.GET_WALLET_TRANSACTIONS, arg?.config);
  },
  fetchGracePeriod: args => {
    return apiGetCall(BFF_API_URLS.GET_USER_GRACE_PERIOD, args?.config);
  },
  updateUser: ({ payload, config }) => {
    return apiPatchCall(BFF_API_URLS.UPDATE_USER, payload, config);
  },
  changeUserEmail: ({ payload, config }) => {
    return apiPatchCall(BFF_API_URLS.CHANGE_USER_EMAIL, payload, config);
  },
  updateContactInfo: ({ payload, config }) => {
    return apiPatchCall(BFF_API_URLS.UPDATE_CONTACT_INFO, payload, config);
  },
  authenticateWithChallenge: ({ payload, config }) => {
    return apiPostCall(BFF_API_URLS.AUTHENTICATE_WITH_CHALLENGE, payload, config);
  },
  getSubdivisions: ({ pathParams, config }) =>
    apiGetCall(BFF_API_URLS.GET_SUBDIVISIONS(pathParams), config),
  checkSession: args => {
    return apiGetCall(BFF_API_URLS.CHECK_SESSION, args?.config);
  },
  agcoVerifyUpdate: ({ payload, config }) => {
    return apiPatchCall(BFF_API_URLS.AGCO_USER_VERIFY_UPDATE, payload, config);
  },
  mobileNumberVerificationRequired: ({ config }) => {
    return apiGetCall(BFF_API_URLS.REGISTER_MOBILE_NUMBER_VERIFICATION_REQUIRED, config);
  },
  limitsRefund: ({ payload, config }) => {
    return apiPutCall(BFF_API_URLS.LIMITS_REFUND, payload, config);
  },
  sendSeonFraudEvent: ({ payload, config }) => {
    return apiPostCall(BFF_API_URLS.SEON_FRAUD_EVENT, payload, config);
  },
};

export default BffApiService;
